import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_MaiImageAiGenerator = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/mai-image-ai-generator',
			name: 'Bing Image Creator — Precise Edits, Consistent Faces by Lift',
			description:
				'Turn a prompt into a photorealistic image with Microsoft Bing image generator inside Lift — precise, localized edits that keep faces and lighting consistent. Describe it, pick a style, and download. Try Lift today!',
			url: 'https://liftapp.ai/mai-image-ai-generator',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{
					'@id': 'https://liftapp.ai/mai-image-ai-generator#product',
				},
				{ '@id': 'https://liftapp.ai/mai-image-ai-generator#howto' },
				{
					'@id': 'https://liftapp.ai/mai-image-ai-generator#benefits',
				},
				{ '@id': 'https://liftapp.ai/mai-image-ai-generator#faq' },
				{
					'@id': 'https://liftapp.ai/mai-image-ai-generator#why-lift',
				},
				{
					'@id': 'https://liftapp.ai/mai-image-ai-generator#explore-tools',
				},
				{
					'@id': 'https://liftapp.ai/mai-image-ai-generator#all-in-one',
				},
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/mai-image-ai-generator#product',
			name: 'Microsoft AI Image Generator',
			description:
				'Turn a prompt into a photorealistic image with Microsoft Bing image generator inside Lift.',
			url: 'https://liftapp.ai/mai-image-ai-generator',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/mai-image-ai-generator#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Describe your image',
					text: 'Type what you want to see — a scene, a poster, a product shot, anything you can put into words',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Choose style, ratio, and model',
					text: 'Select a visual style, set your preferred aspect ratio, and pick an AI model — or optionally upload a reference image to guide the generation',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Let the AI do the work',
					text: 'Watch as Mai Image transforms your text prompt into a unique, high-quality AI-generated image automatically',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/mai-image-ai-generator#benefits',
			name: 'Who can benefit from the Mai Image AI Generator?',
			description:
				"Lift's Mai Image AI Generator is useful for photorealistic detail and precise, localized edits that keep faces and scenes consistent.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: "Portrait & headshot editors — Editing a background or an outfit shouldn't change how a person looks. Mai Image makes precise, localized edits while keeping faces and identity consistent across the result",
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: "Real estate & listing photographers — A small fix, like removing an object or adjusting lighting, shouldn't mean redoing the whole photo. Mai Image updates just the part being adjusted, without disturbing the rest of the image",
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Content creators & bloggers — Blog headers, thumbnails, and social graphics need a distinct look fast. Describe the mood and layout you want and get a finished visual without a design tool',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Small business owners — Flyers, menus, and promo graphics with your own business name and details come together from a single description, no design software required',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Educators & presenters — Custom illustrations and diagrams for slides or course material can be generated to match a specific description instead of being pieced together from stock art',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Mai Image AI Generator Examples by Lift',
				description:
					'Example of an AI image generated with Microsoft Bing image creator using the Lift app.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/mai-image-ai-generator#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Mai Image?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Mai Image is an AI image model that handles both generation and editing in one system, known for photorealistic detail and precise, localized edits that keep faces and scenes consistent. Lift uses it to turn a written prompt into a finished image, with Mai Image 2.5 preselected by default.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Image Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Simply enter your text prompt in Lift's AI image generator, then choose your preferred style, ratio, and model. Optionally upload a reference image to guide the generation. Hit Generate and receive a unique AI-created picture in seconds — no design experience needed.",
					},
				},
				{
					'@type': 'Question',
					name: 'Can I use a different AI model besides Mai Image?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Yes. Lift's image generator supports a range of AI models, so you can switch between them anytime and choose whichever fits your project best.",
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to upload my photos to Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — Lift processes your photos securely and uses them only to generate your results.',
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/mai-image-ai-generator#why-lift',
			name: 'Why Choose the Mai Image AI Generator by Lift',
			description:
				"Lift's Mai Image AI Generator combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Why Choose the Mai Image AI Generator by Lift',
				description:
					'Lift AI image generator powered by Bing image creator — high-quality results, intelligent rendering, cross-platform support, and budget-friendly pricing.',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/mai-image-ai-generator#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/mai-image-ai-generator#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Microsoft AI Image Generator',
					item: 'https://liftapp.ai/mai-image-ai-generator',
				},
			],
		},
	],
};
